import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  CheckCircle,
  XCircle,
  ChevronRight,
  Clock,
  FileText,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { Avatar } from '@brainsway/ui';
import {
  useActivateUser,
} from '../hooks';
import { CreateUserModal } from '../components';
import { UserDetailModal } from '../components/UserDetailModal';
import { BulkDeleteUsersModal } from '../components/BulkDeleteUsersModal';
import type { UserListItem } from '../types';
import toast from 'react-hot-toast';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../../admin/utils/adminAnimations';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useQueryParams } from '@/hooks/useQueryParams';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  SUPER_ADMIN: { bg: '#1e293b', text: '#ffffff' },
  ADMIN: { bg: 'var(--color-primary-500)', text: '#ffffff' },
  MANAGER: { bg: '#e8eaf6', text: '#283593' },
  THERAPIST: { bg: '#e8f5e9', text: '#2e7d32' },
  FINMGR: { bg: '#fff3e0', text: '#e65100' },
  SALESMGR: { bg: '#fce4ec', text: '#c62828' },
  EMP: { bg: '#f3e5f5', text: '#6a1b9a' },
};

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  ACTIVE: { bg: '#ecfdf5', border: '#047857', text: '#047857' },
  PENDING: { bg: '#fffbeb', border: '#b45309', text: '#b45309' },
  INACTIVE: { bg: '#f3f4f6', border: '#9ca3af', text: '#9ca3af' },
};

const ROLES = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'FINMGR', 'SALESMGR', 'EMP'] as const;
const STATUSES = ['ACTIVE', 'PENDING', 'INACTIVE'] as const;

// ═══════════════════════════════════════════════════════════════════
// User Card
// ═══════════════════════════════════════════════════════════════════
function UserCard({
  user,
  fullName,
  isSelected,
  isRTL,
  showRoleBadge,
  onSelect,
  onToggleSelect,
  t,
}: {
  user: UserListItem;
  fullName: string;
  isSelected: boolean;
  isRTL: boolean;
  showRoleBadge: boolean;
  onSelect: () => void;
  onToggleSelect: () => void;
  t: (key: string) => string;
}) {
  return (
    <motion.div
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      className={`glass-surface flex items-center justify-between w-full py-2.5 px-3 md:py-3 md:px-4 rounded-3xl text-start cursor-pointer border border-white/30 hover:bg-white/20 ${isSelected ? 'ring-2 ring-blue-200' : ''}`}
      style={{ boxShadow: ADMIN_COLORS.shadow }}
    >
      {/* Left section */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="flex-shrink-0 flex items-center justify-center cursor-pointer"
          style={{ minWidth: '44px', minHeight: '44px' }}
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            className="w-5 h-5 pointer-events-none"
          />
        </div>
        <Avatar src={user.avatarUrl} name={fullName} size="lg" className="!flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm md:text-base text-neutral-800 truncate max-w-[160px] md:max-w-none">
            {fullName}
          </p>
          {showRoleBadge && (
            <span
              className="inline-block mt-0.5 px-2 py-0.5 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: ROLE_COLORS[user.role]?.bg || '#e5e7eb',
                color: ROLE_COLORS[user.role]?.text || '#000',
                border: '1px solid #e2e8f0',
              }}
            >
              {t(`users.roles.${user.role}`)}
            </span>
          )}
        </div>
      </div>
      {/* Right section */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="hidden md:flex items-center gap-1 text-sm font-medium text-neutral-800">
          <FileText className="w-4 h-4" />
          {user._count.reports}
        </div>
        <StatusDot
          active={user.status === 'ACTIVE'}
          activeColor={STATUS_COLORS[user.status]?.border || '#9ca3af'}
          inactiveColor={STATUS_COLORS[user.status]?.border || '#9ca3af'}
          title={t(`users.statuses.${user.status}`)}
        />
        <ChevronRight
          className="w-5 h-5 text-black/40"
          style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
        />
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export function UsersManagementPage() {
  const { t } = useTranslation();
  const isRTL = useRTL();
  const { hasPermission } = usePermissions();

  const queryClient = useQueryClient();

  const [params, setParams] = useQueryParams({
    q: { default: '' },
    role: { default: '' },
    status: { default: '' },
    user: { default: '' },
    action: { default: '' },
  });

  const createModalOpen = params.action === 'create';

  const [groupByRoles, setGroupByRoles] = useState(true);

  const activateUserMutation = useActivateUser();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['users', 'infinite', params.q, params.role, params.status],
    queryFn: async ({ pageParam }) => {
      const queryParams: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (params.q) queryParams.search = params.q;
      if (params.role) queryParams.role = params.role;
      if (params.status) queryParams.status = params.status;

      const { data } = await http.get<{
        success: boolean;
        users: UserListItem[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>('/users', { params: queryParams });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.users.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allUsers = useMemo(
    () => data?.pages.flatMap((page) => page.users) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allUsers);

  const selectedUser = useMemo(
    () => (params.user ? allUsers.find(u => u.id === params.user) ?? null : null),
    [allUsers, params.user],
  );

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allUsers.filter((u) => u.status === 'ACTIVE').length,
    [allUsers]
  );
  const pendingCount = useMemo(
    () => allUsers.filter((u) => u.status === 'PENDING').length,
    [allUsers]
  );
  const inactiveCount = useMemo(
    () => allUsers.filter((u) => u.status === 'INACTIVE').length,
    [allUsers]
  );

  const ROLE_ORDER = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'THERAPIST', 'FINMGR', 'SALESMGR', 'EMP'];

  const groupedUsers = useMemo(() => {
    if (!groupByRoles) return null;
    const groups: Record<string, UserListItem[]> = {};
    for (const user of allUsers) {
      const role = user.role || 'UNKNOWN';
      if (!groups[role]) groups[role] = [];
      groups[role].push(user);
    }
    return Object.entries(groups).sort(([a], [b]) => {
      const ai = ROLE_ORDER.indexOf(a);
      const bi = ROLE_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [allUsers, groupByRoles]);

  const handleBulkActivate = async () => {
    try {
      triggerHapticFeedback();
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          activateUserMutation.mutateAsync({ id, active: true })
        )
      );
      toast.success(t('users.bulkActivatedSuccess'));
      clear();
    } catch {
      toast.error(t('users.bulkActivateFailed'));
    }
  };

  const handleBulkDeactivate = async () => {
    try {
      triggerHapticFeedback();
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          activateUserMutation.mutateAsync({ id, active: false })
        )
      );
      toast.success(t('users.bulkDeactivatedSuccess'));
      clear();
    } catch {
      toast.error(t('users.bulkDeactivateFailed'));
    }
  };

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    setBulkDeleteOpen(true);
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <PageHeader
        icon={<Users className="w-7 h-7" />}
        title={t('users.management')}
        subtitle={t('users.managementSubtitle', { defaultValue: 'Manage users & permissions' })}
        action={
          hasPermission('action:create_users') ? (
            <CreateButton
              label={t('users.createUser')}
              onClick={() => setParams({ action: 'create' })}
            />
          ) : undefined
        }
      />

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Users className="w-full h-full" />} label={t('users.totalUsers')} value={totalCount} />
        <StatCard icon={<CheckCircle className="w-full h-full" />} label={t('users.activeUsers')} value={activeCount} />
        <StatCard icon={<Clock className="w-full h-full" />} label={t('users.pendingUsers')} value={pendingCount} />
        <StatCard icon={<XCircle className="w-full h-full" />} label={t('users.inactiveUsers')} value={inactiveCount} />
      </motion.div>

      {/* ─── Search & Filters ────────────────────────────────── */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters', { defaultValue: 'Filters' })}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col md:flex-row gap-3">
          <SearchInput
            value={params.q}
            onChange={(v) => setParams({ q: v })}
            placeholder={t('users.searchPlaceholder')}
          />

          <button
            onClick={selectAll}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={selectAll}
              className="w-5 h-5 pointer-events-none"
              readOnly
            />
            <span className="font-bold text-sm">{t('users.selectAll')}</span>
          </button>

          <GlassSelect
            value={params.role}
            onChange={(e) => {
              setParams({ role: e.target.value });
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('users.allRoles')}</option>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {t(`users.roles.${role}`)}
              </option>
            ))}
          </GlassSelect>

          <GlassSelect
            value={params.status}
            onChange={(e) => {
              setParams({ status: e.target.value });
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('users.allStatuses')}</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`users.statuses.${status}`)}
              </option>
            ))}
          </GlassSelect>

          <label
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors select-none"
          >
            <input
              type="checkbox"
              checked={groupByRoles}
              onChange={() => {
                setGroupByRoles((v) => !v);
                triggerHapticFeedback();
              }}
              className="w-5 h-5"
            />
            <span className="font-bold text-sm whitespace-nowrap">{t('users.groupByRoles')}</span>
          </label>
        </div>
      </motion.div>

      {/* ─── User List ───────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('users.loadError')} />
        ) : allUsers.length === 0 ? (
          <EmptyState
            icon={<Users className="w-full h-full" />}
            message={t('users.noUsers')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {groupByRoles && groupedUsers ? (
              groupedUsers.map(([role, users]) => (
                <div key={role} className="space-y-2">
                  {/* Role group header */}
                  <div className="flex items-center gap-2 px-1 pt-2">
                    <span
                      className="px-3 py-1 rounded-lg text-xs font-bold"
                      style={{
                        backgroundColor: ROLE_COLORS[role]?.bg || '#e5e7eb',
                        color: ROLE_COLORS[role]?.text || '#000',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {t(`users.roles.${role}`)}
                    </span>
                    <span className="text-xs font-semibold text-slate-400">
                      {users.length}
                    </span>
                  </div>
                  {users.map((user) => {
                    const fullName =
                      user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.firstName || user.lastName || user.email;
                    const selected = isSelected(user.id);
                    return (
                      <UserCard
                        key={user.id}
                        user={user}
                        fullName={fullName}
                        isSelected={selected}
                        isRTL={isRTL}
                        showRoleBadge={false}
                        onSelect={() => {
                          triggerHapticFeedback();
                          setParams({ user: user.id });
                        }}
                        onToggleSelect={() => toggle(user.id)}
                        t={t}
                      />
                    );
                  })}
                </div>
              ))
            ) : (
              allUsers.map((user) => {
                const fullName =
                  user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.firstName || user.lastName || user.email;
                const selected = isSelected(user.id);
                return (
                  <UserCard
                    key={user.id}
                    user={user}
                    fullName={fullName}
                    isSelected={selected}
                    isRTL={isRTL}
                    showRoleBadge
                    onSelect={() => {
                      triggerHapticFeedback();
                      setParams({ user: user.id });
                    }}
                    onToggleSelect={() => toggle(user.id)}
                    t={t}
                  />
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Bulk Action Bar — Mushroom buttons ───────────────── */}
      <BulkActionBar
        count={count}
        onDelete={handleBulkDelete}
        onClear={clear}
        isDeleting={false}
        onDeactivate={handleBulkDeactivate}
        deactivateLabel={t('users.deactivate')}
        isDeactivating={activateUserMutation.isPending}
        onActivate={handleBulkActivate}
        activateLabel={t('users.activate')}
        isActivating={activateUserMutation.isPending}
      />

      {/* ─── Infinite scroll sentinel ─────────────────────────── */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />

      {/* ─── Modals ───────────────────────────────────────────── */}
      <CreateUserModal
        isOpen={createModalOpen}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['users'] }); setParams({ action: '' }); }}
        onSuccess={() => {}}
      />

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['users'] }); setParams({ user: '' }); }}
        />
      )}

      <BulkDeleteUsersModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        userIds={Array.from(selectedIds)}
        onDeleted={clear}
      />

    </motion.div>
  );
}
